import { DateTime, Duration } from 'luxon';
import { pick } from 'lodash/fp';
import { IntlShape } from 'react-intl';
import {
  TimeSignal,
  LabelValueSource,
  LabelValue,
  LabelValueDetailsInput,
  LabelType,
  LabelValueDetails,
  LabelSuggestionDetails,
  LabelSuggestionsQuery,
  LabelOrigin
} from 'graphql/types';
import {
  CustomLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { CustomLabelsField } from 'modules/customLabels/hooks';
import { SuggestedLabelValue } from 'modules/timesheet-labels/hooks';
import { determineLabelAcceptedBy } from 'modules/timesheet-labels/hooks/utils/labelUtils';
import { NoteFormData, NoteData, TimesheetLabelFields } from '../noteSchema';

export const picker = pick(['idValue', 'numericValue', 'stringValue']);

export const mapTimeSignalToNoteFormData = ({
  timeSignal,
  timesheetLabels,
  customLabels,
  isSmartSuggestionEnabled
}: {
  timeSignal: TimeSignal | null;
  timesheetLabels: TimesheetLabel[];
  customLabels: CustomLabel[];
  isSmartSuggestionEnabled: boolean;
}): NoteFormData => {
  if (timeSignal) {
    return {
      id: timeSignal.id,
      note: timeSignal.description,
      startTime: timeSignal.startTime ? new Date(timeSignal.startTime) : null,
      endTime: timeSignal.endTime ? new Date(timeSignal.endTime) : null,
      duration: timeSignal.duration || 0,
      timesheetLabels: mapTimesheetLabelsToFormFields(
        isSmartSuggestionEnabled,
        timesheetLabels
      ),
      customLabels: mapCustomLabelsToFormFields(
        isSmartSuggestionEnabled,
        customLabels
      ),
      classification: timeSignal.classification,
      date: timeSignal.date
        ? DateTime.fromISO(timeSignal.date).toJSDate()
        : null,
      labelValuesInferenceId: timeSignal.labelValuesInferenceId
    };
  }
  return {
    note: '',
    startTime: null,
    endTime: null,
    duration: 0,
    timesheetLabels: mapTimesheetLabelsToFormFields(
      isSmartSuggestionEnabled,
      timesheetLabels
    ),
    customLabels: []
  };
};

export const mapTimeSignalToNoteData = (timeSignal: TimeSignal): NoteData => {
  return {
    id: timeSignal.id,
    note: timeSignal.description,
    startTime: timeSignal.startTime ? new Date(timeSignal.startTime) : null,
    endTime: timeSignal.endTime ? new Date(timeSignal.endTime) : null,
    duration: timeSignal.duration || 0,
    classification: timeSignal.classification,
    date: timeSignal.date ? DateTime.fromISO(timeSignal.date).toJSDate() : null,
    labelValuesInferenceId: timeSignal.labelValuesInferenceId
  };
};

export const mapCustomLabelsToFormFields = (
  isSmartSuggestionEnabled: boolean,
  customLabels: TimesheetLabel[] = []
): CustomLabel[] => {
  return customLabels.map((timesheetLabel: TimesheetLabel) =>
    mapCustomLabelToFormField(isSmartSuggestionEnabled, timesheetLabel)
  );
};

export const mapTimesheetLabelsToFormFields = (
  isSmartSuggestionEnabled: boolean,
  timesheetLabels: TimesheetLabel[] = []
): TimesheetLabelFields => {
  return timesheetLabels.map((timesheetLabel: TimesheetLabel) =>
    mapTimesheetLabelToFormField(isSmartSuggestionEnabled, timesheetLabel)
  );
};

export const mapFormFieldsToTimesheetLabels = (
  fields: TimesheetLabelFields = []
): TimesheetLabel[] => {
  return fields.map((field) => {
    return field.label;
  });
};

export const mapTimesheetLabelToFormField = (
  isSmartSuggestionEnabled: boolean,
  timesheetLabel: TimesheetLabel
): Record<string, TimesheetLabel> => {
  const getValueSource = () => {
    return isSmartSuggestionEnabled
      ? timesheetLabel.valueSource
        ? timesheetLabel.valueSource
        : LabelValueSource.Suggestion
      : LabelValueSource.User;
  };

  const retLabel = {
    label: {
      ...timesheetLabel,
      value: timesheetLabel?.value
        ? {
            idValue: timesheetLabel?.value?.idValue ?? null,
            numericValue: timesheetLabel?.value?.numericValue ?? null,
            stringValue: timesheetLabel?.value?.stringValue ?? null
          }
        : null,
      valueSource: getValueSource(),
      acceptedBy: determineLabelAcceptedBy(
        getValueSource(),
        timesheetLabel.acceptedBy,
        isSmartSuggestionEnabled
      )
    }
  };
  if (isSmartSuggestionEnabled) {
    retLabel.label.suggestedValues = timesheetLabel.suggestedValues;
    retLabel.label.suggestionMetadata = timesheetLabel.suggestionMetadata;
  }
  return retLabel;
};

export const mapCustomLabelToFormField = (
  isSmartSuggestionEnabled: boolean,
  timesheetLabel: TimesheetLabel
): CustomLabel => {
  const retLabel = {
    ...timesheetLabel,
    value: timesheetLabel?.value
      ? {
          idValue: timesheetLabel?.value?.idValue ?? null,
          numericValue: timesheetLabel?.value?.numericValue ?? null,
          stringValue: timesheetLabel?.value?.stringValue ?? null
        }
      : null,
    valueSource: isSmartSuggestionEnabled
      ? timesheetLabel.valueSource
        ? timesheetLabel.valueSource
        : LabelValueSource.Suggestion
      : LabelValueSource.User
  };
  retLabel.acceptedBy = determineLabelAcceptedBy(
    retLabel.valueSource,
    timesheetLabel.acceptedBy,
    isSmartSuggestionEnabled
  );
  if (isSmartSuggestionEnabled) {
    retLabel.suggestedValues = timesheetLabel.suggestedValues;
    retLabel.suggestionMetadata = timesheetLabel.suggestionMetadata;
  }
  return retLabel;
};

const getLabelValueFromTimesheetLabel = (
  label: TimesheetLabel
): LabelValue | null => {
  if (
    label.type === LabelType.Numeric &&
    (label.value?.numericValue ||
      !Number.isNaN(Number(label.value?.numericValue)))
  ) {
    return {
      idValue: label.value?.idValue,
      stringValue: label.value?.stringValue,
      numericValue: label.value?.numericValue
    };
  }
  return label.value?.idValue || label.value?.stringValue
    ? {
        idValue: label.value?.idValue,
        stringValue: label.value?.stringValue,
        numericValue: label.value?.numericValue
      }
    : null;
};

export const mapTimesheetLabel = (
  isSmartSuggestionEnabled: boolean,
  label: TimesheetLabel
): LabelValueDetailsInput => {
  const retValue: LabelValueDetailsInput = {
    labelId: label.id,
    value: label.value ? getLabelValueFromTimesheetLabel(label) : null
  };

  if (isSmartSuggestionEnabled) {
    retValue.suggestedValues = label.suggestedValues;
    retValue.suggestionMetadata = label.suggestionMetadata;
    retValue.valueSource = label.valueSource;
  }
  retValue.acceptedBy = determineLabelAcceptedBy(
    retValue.valueSource ?? LabelValueSource.User,
    label.acceptedBy,
    isSmartSuggestionEnabled
  );
  return retValue;
};

export const mergeResetLabels = (
  defaultValues: NoteFormData,
  values: NoteFormData
): TimesheetLabelFields | null | [] => {
  if (defaultValues.timesheetLabels?.length) {
    if (!values.timesheetLabels?.length) {
      return defaultValues.timesheetLabels;
    } else {
      const labelsChanged =
        JSON.stringify(values.timesheetLabels) !==
        JSON.stringify(defaultValues.timesheetLabels);
      if (labelsChanged) {
        // copy comment and duration labels as it is
        return defaultValues.timesheetLabels.map((dl) => {
          if ([LabelType.Comment].includes(dl.label?.type)) {
            const valueLabel = values.timesheetLabels!.find(
              (l: any) => l?.label?.type === dl.label?.type
            );
            if (valueLabel) {
              dl.label.value = valueLabel.label?.value;
            }
          } else {
            //if labels ids are not changed keep them
            const valueLabel = values.timesheetLabels!.find(
              (l: any) => l?.label?.id === dl.label?.id
            );
            if (valueLabel) {
              dl.label.value = valueLabel.label?.value;
              dl.label.valueSource = valueLabel.label?.valueSource;
              dl.label.acceptedBy = valueLabel.label?.acceptedBy;
            }
          }
          return dl;
        });
      }
      return null;
    }
  }
  return [];
};

export const getCustomLabel = (
  suggestedCustomLables: LabelSuggestionDetails[],
  selectedCustomLabels: CustomLabelsField[],
  getSuggestions: (
    suggestions: LabelSuggestionsQuery | undefined,
    type: string | undefined
  ) => SuggestedLabelValue,
  labelSuggestions: LabelSuggestionsQuery | undefined,
  isTimeSignalFederatedSuggestionsEnabled: boolean
): TimesheetLabel[] => {
  const finalsuggestedCustomLabels = suggestedCustomLables?.filter(
    (label) =>
      !selectedCustomLabels.some(
        (item: CustomLabel) => item.id === label.label.id
      )
  );

  const suggCustomLabel = finalsuggestedCustomLabels.map((item) => {
    const { labelValue, metadata } = getSuggestions(
      labelSuggestions,
      item.label.id
    );
    return {
      id: item.label.id!,
      name: item.label.name!,
      origin: LabelOrigin.User,
      valueSource: LabelValueSource.Suggestion,
      type: item.label.type,
      value: {
        idValue: isTimeSignalFederatedSuggestionsEnabled
          ? undefined
          : item.suggestedValues[0]?.idValue,
        stringValue: isTimeSignalFederatedSuggestionsEnabled
          ? undefined
          : item.suggestedValues[0]?.stringValue
      },
      suggestedValues: labelValue,
      suggestionMetadata: metadata
    };
  });
  return [...suggCustomLabel, ...selectedCustomLabels];
};

export const mapLabelValuesToTimesheetLabels = ({
  isSmartSuggestionEnabled,
  labelValues
}: {
  labelValues: LabelValueDetails[];
  isSmartSuggestionEnabled: boolean;
}): TimesheetLabel[] => {
  return labelValues.map((labelValue) => {
    const returnValue: TimesheetLabel = {
      ...labelValue.label,
      value: labelValue?.value ? picker(labelValue.value) : null
    };
    if (isSmartSuggestionEnabled) {
      returnValue.valueSource = labelValue?.valueSource;
      returnValue.suggestedValues = labelValue?.suggestedValues
        ? (labelValue?.suggestedValues.map(picker) as [LabelValue])
        : undefined;
      returnValue.suggestionMetadata =
        labelValue?.suggestionMetadata || undefined;
    }
    returnValue.acceptedBy = determineLabelAcceptedBy(
      returnValue.valueSource ?? LabelValueSource.User,
      labelValue.acceptedBy,
      isSmartSuggestionEnabled
    );
    return returnValue;
  });
};

export const getDurationSecondsFormFormat = (
  duration?: string | null,
  intl?: IntlShape
): number | null | undefined => {
  if (!duration || !intl) {
    return null;
  }

  const strSplit = duration.split(':');
  if (strSplit.length < 3) {
    return null;
  }
  const [hours, minutes, secondsWithHrs] = strSplit;
  const [seconds] = secondsWithHrs.split(
    intl.formatMessage({ id: 'note.hoursSuffix' })
  );

  return Duration.fromObject({
    hours: +hours,
    minutes: +minutes,
    seconds: +seconds
  })
    .shiftTo('seconds')
    .toObject().seconds;
};

export const defaultDateTime = (): DateTime => DateTime.local();

export const truncateMessage = (message: string, limit = 50): string =>
  message.length <= limit ? message : message.substring(0, limit).concat('...');
